import { AxiosError } from 'axios';
import { useActiveCohortSessionsStore } from '../stores/active-cohort-sessions.store';
import type {
  AvailabilityErrorCode,
  AvailabilityResult,
  CohortListener,
  MyAvailability,
  SaveAvailabilityInput,
} from '../types/availability';
import { deviceTimeZone } from '../utils/listening';
import api from './api';

/**
 * Listening hours. Every call RETURNS its refusal (class B): the server's
 * `error` code and `message` reach the caller, which shows them where they
 * belong (an overlap under the weekly hours, a length error under the
 * lengths) rather than a generic toast.
 */

const FALLBACK = "Couldn't reach the server. Check your connection and try again.";

/** The app-wide poll carries the ring and the notice (P7d); a save may change both. */
function refreshListeningFeed(): void {
  void useActiveCohortSessionsStore.getState().refresh();
}

function refusal<T>(error: unknown): AvailabilityResult<T> {
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as { error?: string; message?: string };
    if (typeof data.error === 'string') {
      return {
        success: false,
        error: data.error as AvailabilityErrorCode,
        message: data.message ?? FALLBACK,
      };
    }
  }
  return { success: false, error: 'network', message: FALLBACK };
}

export async function getMyAvailability(): Promise<AvailabilityResult<MyAvailability>> {
  try {
    const response = await api.get<{ data: MyAvailability }>('/me/availability');
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/** Replaces the whole schedule; the answer is the stored schedule. */
export async function saveMyAvailability(
  input: SaveAvailabilityInput,
): Promise<AvailabilityResult<MyAvailability>> {
  try {
    const response = await api.put<{ data: MyAvailability }>('/me/availability', input);
    // P7d (D51): hours that cover now come with a "You're available" notice;
    // fetch the shared poll now so the toast shows without waiting for it.
    refreshListeningFeed();
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * "Pause for today" (B6a): scheduled on-demand availability off for the rest
 * of the giver's local today, in every group and 1-on-1. The answer is the
 * new GET me/availability state. Refused `no_schedule` without hours.
 */
export async function pauseListeningToday(): Promise<AvailabilityResult<MyAvailability>> {
  try {
    const response = await api.post<{ data: MyAvailability }>('/me/availability/pause-today');
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/** Undo "Pause for today". */
export async function resumeListeningToday(): Promise<AvailabilityResult<MyAvailability>> {
  try {
    const response = await api.delete<{ data: MyAvailability }>('/me/availability/pause-today');
    refreshListeningFeed();
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * "Also ping me outside my hours" (B5b) alone: PUT without `windows` keeps
 * the stored schedule.
 */
export async function savePingOutsideHours(
  on: boolean,
): Promise<AvailabilityResult<MyAvailability>> {
  try {
    const response = await api.put<{ data: MyAvailability }>('/me/availability', {
      ping_outside_hours: on,
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/**
 * Who is listening in this group (members only; the server applies X4 and
 * blocks). Sends the device's zone as `tz` (D19), so a web viewer — whose
 * account has no zone — reads the hours on their own clock.
 */
export async function getCohortListeners(
  cohortId: number,
): Promise<AvailabilityResult<CohortListener[]>> {
  try {
    const tz = deviceTimeZone();
    const response = await api.get<{ data: { listeners: CohortListener[] } }>(
      `/cohorts/${cohortId}/listeners`,
      tz ? { params: { tz } } : undefined,
    );
    return { success: true, data: response.data.data.listeners };
  } catch (error) {
    return refusal(error);
  }
}
